export type PrimitiveValue = string | number | boolean | null;

export type QueryParamValue = PrimitiveValue | undefined;

export interface ProductExceptionItem {
    productClassId: string;
    type: number;
    qty: number;
    unit: string;
    price: string;
    appType: string;
}

export interface UpdateUrlParamsOptions {
    reload?: boolean;
    replace?: boolean;
}

export interface NumberSeparatorParams {
    num: string | number | null | undefined;
    points?: number;
    unitChar?: string;
}

export interface NumberRoundParams {
    num: string | number | null | undefined;
    points?: number;
}

export interface ValidateOptions {
    emptyMsg?: string;
    formatMsg?: string;
}

export interface PhoneParserResult {
    tel: string;
    mobile: string;
}

export interface CookieOptions {
    key: string;
    value: string | number | boolean;
    path?: string;
    maxAge?: number;
    expires?: Date;
    secure?: boolean;
    sameSite?: 'Strict' | 'Lax' | 'None';
}

export interface GoHomeOptions {
    key?: string;
    value?: string | number | boolean;
}

export type TextInputValue = string | number | '';

/**
 * 延遲執行指定函式
 */
export const sleep = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * 清理文字型輸入值
 */
export const normalizeTextInputValue = (value: unknown): TextInputValue => {
    if (typeof value === 'number') return value;

    if (typeof value === 'string') {
        const text = value.trim();
        return text ? text : '';
    }

    return '';
};

/**
 * 深拷貝資料
 */
export const deepClone = <T>(value: T): T => {
    return JSON.parse(JSON.stringify(value)) as T;
};

/**
 * 將輸入值轉為數字，轉換失敗時回傳 fallback
 */
export const toNumber = (value: unknown, fallback = 0): number => {
    if (value === null || value === undefined) {
        return fallback;
    }

    const normalizedValue =
        typeof value === 'string' ? value.replace(/,/g, '').trim() : value;
    if (normalizedValue === '') {
        return fallback;
    }

    const parsed = Number(normalizedValue);
    return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * 判斷文字是否有內容 (去除前後空白)
 */
export const hasText = (value: unknown): boolean => {
    return String(value ?? '').trim().length > 0;
};

/**
 * 是否有新版商品內容頁
 */
export const hasProductDetail = (type: string | number): boolean => {
    const productDetailList = [700];
    const typeValue = Number(type);

    return productDetailList.includes(typeValue);
};

/**
 * 例外處理商品項目
 */
export const getProductExceptionItem = (
    type: string | number
): ProductExceptionItem | undefined => {
    const productExceptionList: ProductExceptionItem[] = [
        {
            productClassId: 'RGBBook',
            type: 700,
            qty: 10,
            unit: '本',
            price: '超值優惠價格',
            appType: '',
        },
    ];

    const typeValue = Number(type);
    return productExceptionList.find((item) => item.type === typeValue);
};

/**
 * 判斷輸入值是否為有效數字
 */
export const isValidNumber = (
    value: string | number | null | undefined
): boolean => {
    return value !== '' && !Number.isNaN(Number(value));
};

/**
 * 更新 URL query 參數
 */
export const updateUrlParams = (
    newParams: Record<string, QueryParamValue>,
    options: UpdateUrlParamsOptions = {
        reload: false,
        replace: true,
    }
): void => {
    const locationUrl = new URL(window.location.href);
    const params = locationUrl.searchParams;

    Object.entries(newParams).forEach(([key, value]) => {
        if (value === null || typeof value === 'undefined') {
            params.delete(key);
            return;
        }

        params.set(key, String(value));
    });

    locationUrl.search = params.toString();

    if (options.reload) {
        window.location.href = locationUrl.toString();
        return;
    }

    if (options.replace ?? true) {
        window.history.replaceState({}, '', locationUrl);
    } else {
        window.history.pushState({}, '', locationUrl);
    }
};

/**
 * 解析 mappingCode 規格
 */
export const mappingCodeParser = (
    mappingCode: string
): Record<string, string> => {
    if (!mappingCode) return {};

    return mappingCode
        .split('###')
        .reduce<Record<string, string>>((result, item) => {
            const [key = '', value = ''] = item.split('=');
            if (key) {
                result[key] = value;
            }
            return result;
        }, {});
};

/**
 * 解析 scSubName 規格
 */
export const scSubNameParser = (
    scSubName: string
): Array<{ label: string; value: string }> => {
    if (!scSubName) return [];

    return scSubName.split('###').map((item) => {
        const [label = '', value = ''] = item.split('：');
        return {
            label,
            value,
        };
    });
};

/**
 * 組合 mappingCode 規格
 */
export const mappingCodeCombiner = (
    optionModel: Record<string, string | number | boolean | null | undefined>
): string => {
    return Object.entries(optionModel).reduce((result, [key, value]) => {
        const formatted = `${key}=${value ?? ''}`;
        if (!result) {
            return formatted;
        }

        return `${result}###${formatted}`;
    }, '');
};

/**
 * 將數字每 n 位插入分隔符
 */
export const numberSeparator = ({
    num,
    points = 3,
    unitChar = ',',
}: NumberSeparatorParams): string => {
    if (num === null || typeof num === 'undefined') {
        return '';
    }

    const originalValue = String(num);
    const numericValue = Number(originalValue.replace(/,/g, ''));
    if (!isValidNumber(numericValue)) {
        return originalValue;
    }

    const [integerPart = '', decimalPart] = originalValue.split('.');
    const regExp = new RegExp(`\\B(?=(\\d{${points}})+(?!\\d))`, 'g');
    const formattedInteger = integerPart.replace(regExp, unitChar);

    if (decimalPart) {
        return `${formattedInteger}.${decimalPart}`;
    }

    return formattedInteger;
};

/**
 * 將數字四捨五入至指定位數
 */
export const numberRound = ({
    num,
    points = 3,
}: NumberRoundParams): number | string | null | undefined => {
    if (num === null || typeof num === 'undefined') {
        return '';
    }

    const originalValue = num;
    const numericValue = Number(String(num).replace(/,/g, ''));
    if (!isValidNumber(numericValue)) {
        return originalValue;
    }

    const multiplier = Math.pow(10, points);
    return Math.round(numericValue * multiplier) / multiplier;
};

/**
 * 驗證電子信箱
 */
export const validateEmail = (
    value: string,
    { emptyMsg, formatMsg }: ValidateOptions = {}
): string => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedValue = value.trim();

    if (!normalizedValue) {
        return emptyMsg || '請填寫完整 Email';
    }

    if (normalizedValue.length > 254) {
        return formatMsg || '請填寫正確發票 Email 格式';
    }

    if (!emailRegex.test(value)) {
        return formatMsg || '請填寫正確發票 Email 格式';
    }

    return '';
};

/**
 * 驗證電話
 */
export const validatePhone = (
    value: string,
    { emptyMsg, formatMsg }: ValidateOptions = {}
): string => {
    const mobileRegex = /^09\d{2}-?\d{6}$/;
    const telRegex =
        /^((02-?\d{8})|(03\d-?\d{7,8})|(04-?\d{7,8})|(05-?\d{6,8})|(06-?\d{7,8})|(07-?\d{7,8})|(08-?\d{7,8})|(082-?\d{6,7})|(0836-?\d{6}))$/;

    if (!value) {
        return emptyMsg || '請填寫完整聯絡電話';
    }
    if (!(mobileRegex.test(value) || telRegex.test(value))) {
        return formatMsg || '請填寫正確聯絡電話格式';
    }

    return '';
};

/**
 * 解析電話為市話或手機
 */
export const phoneParser = (value: string): PhoneParserResult => {
    const mobileRegex = /^09\d{2}-?\d{6}$/;
    const telRegex =
        /^((02-?\d{8})|(03\d-?\d{7,8})|(04-?\d{7,8})|(05-?\d{6,8})|(06-?\d{7,8})|(07-?\d{7,8})|(08-?\d{7,8})|(082-?\d{6,7})|(0836-?\d{6}))$/;

    if (!value) {
        return {
            tel: '',
            mobile: '',
        };
    }

    if (mobileRegex.test(value)) {
        return {
            tel: '',
            mobile: value,
        };
    }

    if (telRegex.test(value)) {
        return {
            tel: value,
            mobile: '',
        };
    }

    return {
        tel: '',
        mobile: '',
    };
};

/**
 * 設定 Cookie
 */
export const setCookie = ({
    key,
    value,
    path = '/',
    maxAge,
    expires,
    secure = false,
    sameSite = 'Lax',
}: CookieOptions): void => {
    if (!key) {
        throw new Error('Cookie key is required');
    }

    let cookieValue = `${key}=${value}; path=${path}; SameSite=${sameSite}`;

    if (maxAge) {
        cookieValue += `; max-age=${maxAge}`;
    }
    if (expires instanceof Date) {
        cookieValue += `; expires=${expires.toUTCString()}`;
    }
    if (secure) {
        cookieValue += '; Secure';
    }

    document.cookie = cookieValue;
};

/**
 * 設定 Cookie 後導回首頁
 */
export const goHome = ({ key, value }: GoHomeOptions = {}): void => {
    if (key && value) {
        setCookie({ key, value });
    }

    window.location.href = '/';
};
